import { type ChangeEvent, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Link } from "react-router-dom";
import { debounce } from "lodash";
import {
  Copy,
  Download,
  ExternalLink,
  Mail,
  Plus,
  Search,
  X,
} from "lucide-react";

import { Input } from "@components/ui/Input";

import { adminGet } from "@lib/adminApi";

import type {
  Gift,
  GiftDailyPoint,
  GiftListResponse,
  GiftStats,
} from "./types";

const STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-800",
  sent: "bg-green-100 text-green-800",
  used: "bg-blue-100 text-blue-800",
};

const PAGE_SIZE = 25;
// Max rows a CSV export will pull (in PAGE_SIZE_EXPORT chunks). Keeps an
// "export everything" click from firing hundreds of requests against the 72k+
// legacy backlog — narrow the filters to export more than this.
const EXPORT_CAP = 5000;
const EXPORT_PAGE = 250; // matches the API's max page size

// Column header label -> backend sort key. Labels not present here aren't
// sortable (Gift Code, Status [derived from sent/used], Actions).
const SORT_KEYS: Record<string, string> = {
  ID: "id",
  Purchaser: "purchaserEmail",
  Recipient: "to",
  Product: "purchaseOption",
  Delivery: "printOrSend",
  Created: "createdAt",
  Scheduled: "scheduledAt",
};

const COLUMNS = [
  "ID",
  "Gift Code",
  "Purchaser",
  "Recipient",
  "Product",
  "Delivery",
  "Status",
  "Created",
  "Scheduled",
  "Actions",
];

const StatCard = ({
  label,
  value,
  sub,
}: {
  label: string;
  value: number;
  sub?: string;
}) => (
  <div className="border rounded-lg bg-white px-4 py-3">
    <div className="text-xs text-gray-500">{label}</div>
    <div className="text-2xl font-bold">{value.toLocaleString()}</div>
    {sub && <div className="text-xs text-gray-400">{sub}</div>}
  </div>
);

// Dependency-free bar sparkline of daily native gift purchases. A continuing
// row of bars = healthy; a flatline at the right edge = purchases stopped.
const Sparkline = ({ points }: { points: GiftDailyPoint[] }) => {
  if (points.length === 0) return null;
  const max = Math.max(1, ...points.map((p) => p.count));
  const total = points.reduce((sum, p) => sum + p.count, 0);
  const H = 40;
  const barW = 100 / points.length;
  return (
    <div className="border rounded-lg bg-white px-4 py-3">
      <div className="flex items-baseline justify-between">
        <span className="text-xs text-gray-500">
          New gift purchases · last {points.length} days
        </span>
        <span className="text-xs text-gray-400">
          {total.toLocaleString()} total
        </span>
      </div>
      <svg
        viewBox={`0 0 100 ${H}`}
        preserveAspectRatio="none"
        className="mt-2 h-12 w-full text-cta"
        role="img"
        aria-label={`New gift purchases per day, last ${points.length} days`}
      >
        {points.map((p, i) => {
          const h = (p.count / max) * H;
          return (
            <rect
              key={p.date}
              x={i * barW + barW * 0.15}
              y={H - h}
              width={barW * 0.7}
              height={h}
              className="fill-current"
            >
              <title>{`${p.date}: ${p.count}`}</title>
            </rect>
          );
        })}
      </svg>
    </div>
  );
};

const csvCell = (v: unknown) => {
  const s = v == null ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export const GiftsList = () => {
  const [gifts, setGifts] = useState<Gift[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [deliveryFilter, setDeliveryFilter] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState("id");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [stats, setStats] = useState<GiftStats | null>(null);
  const [daily, setDaily] = useState<GiftDailyPoint[]>([]);
  const [exporting, setExporting] = useState(false);
  const navigate = useNavigate();

  const fetchGifts = useCallback(
    async (
      query: string,
      status: string,
      delivery: string,
      page: number,
      sort: string,
      order: string,
    ) => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (query) params.set("query", query);
        if (status) params.set("status", status);
        if (delivery) params.set("deliveryType", delivery);
        params.set("limit", String(PAGE_SIZE));
        params.set("offset", String((page - 1) * PAGE_SIZE));
        params.set("sortBy", sort);
        params.set("sortOrder", order);

        const result = await adminGet<GiftListResponse>(
          `/admin/gifts?${params}`,
        );
        setGifts(result.gifts);
        setTotal(result.total);
      } catch (err) {
        console.error("Failed to fetch gifts:", err);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const debouncedFetch = useCallback(
    debounce(
      (q: string, s: string, d: string, p: number, sb: string, so: string) =>
        fetchGifts(q, s, d, p, sb, so),
      300,
    ),
    [fetchGifts],
  );

  useEffect(() => {
    debouncedFetch(
      searchQuery,
      statusFilter,
      deliveryFilter,
      currentPage,
      sortBy,
      sortOrder,
    );
  }, [
    searchQuery,
    statusFilter,
    deliveryFilter,
    currentPage,
    sortBy,
    sortOrder,
    debouncedFetch,
  ]);

  // Summary stat cards — overall (unfiltered) counts. Fetched once on mount.
  useEffect(() => {
    adminGet<GiftStats>("/admin/gifts/stats")
      .then(setStats)
      .catch((err) => console.error("Failed to fetch gift stats:", err));
  }, []);

  // Last-30-days sparkline (native gifts only). Fetched once on mount.
  useEffect(() => {
    adminGet<GiftDailyPoint[]>("/admin/gifts/daily-stats")
      .then(setDaily)
      .catch((err) => console.error("Failed to fetch gift daily stats:", err));
  }, []);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const formatDate = (iso: string | null) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatDateTime = (iso?: string | null) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const handleSort = (label: string) => {
    const key = SORT_KEYS[label];
    if (!key) return;
    if (sortBy === key) {
      setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortOrder("desc");
    }
    setCurrentPage(1);
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const rows: Gift[] = [];
      let offset = 0;
      // Loop the same filtered/sorted query in EXPORT_PAGE chunks up to the cap.
      while (rows.length < EXPORT_CAP) {
        const params = new URLSearchParams();
        if (searchQuery) params.set("query", searchQuery);
        if (statusFilter) params.set("status", statusFilter);
        if (deliveryFilter) params.set("deliveryType", deliveryFilter);
        params.set("limit", String(EXPORT_PAGE));
        params.set("offset", String(offset));
        params.set("sortBy", sortBy);
        params.set("sortOrder", sortOrder);
        const res = await adminGet<GiftListResponse>(`/admin/gifts?${params}`);
        rows.push(...res.gifts);
        offset += EXPORT_PAGE;
        if (res.gifts.length < EXPORT_PAGE || offset >= res.total) break;
      }

      const capped = rows.slice(0, EXPORT_CAP);
      const headers = [
        "id",
        "code",
        "purchaserEmail",
        "recipientEmail",
        "senderName",
        "product",
        "deliveryType",
        "status",
        "createdAt",
        "scheduledAt",
        "legacyId",
      ];
      const lines = [headers.join(",")];
      for (const g of capped) {
        lines.push(
          [
            g.id,
            g.code,
            g.purchaserEmail,
            g.recipientEmail ?? "",
            g.senderName,
            g.product,
            g.deliveryType,
            g.status,
            g.createdAt ?? "",
            g.scheduledAt ?? "",
            g.legacyId ?? "",
          ]
            .map(csvCell)
            .join(","),
        );
      }
      const blob = new Blob([lines.join("\n")], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `gifts-export-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);

      if (rows.length >= EXPORT_CAP) {
        alert(
          `Exported the first ${EXPORT_CAP.toLocaleString()} rows. Narrow the filters to export more.`,
        );
      }
    } catch (err) {
      console.error("CSV export failed:", err);
      alert("Export failed — see console for details.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Gift Management</h2>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500">{total} total gifts</span>
          <button
            onClick={exportCsv}
            disabled={exporting || total === 0}
            className="flex items-center gap-1 px-3 py-2 text-sm font-medium border rounded-full hover:bg-gray-50 disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            {exporting ? "Exporting…" : "Export CSV"}
          </button>
          <Link
            to="batch"
            className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-white bg-cta hover:bg-cta-hover rounded-full"
          >
            <Plus className="h-4 w-4" /> Generate gift codes
          </Link>
        </div>
      </div>

      {/* Summary cards (overall, unfiltered) */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Total gifts" value={stats.total} />
          <StatCard
            label="Redeemed"
            value={stats.redeemed}
            sub={
              stats.total
                ? `${Math.round((stats.redeemed / stats.total) * 100)}% redemption`
                : undefined
            }
          />
          <StatCard label="Sent (unredeemed)" value={stats.sent} />
          <StatCard label="Pending" value={stats.pending} />
        </div>
      )}

      <Sparkline points={daily} />

      {/* Filters */}
      <div className="flex gap-3 items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            className="pl-9"
            placeholder="Search by code, email, or name..."
            value={searchQuery}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setSearchQuery(e.target.value)
            }
          />
          {searchQuery && (
            <button
              className="absolute right-3 top-1/2 -translate-y-1/2"
              onClick={() => setSearchQuery("")}
            >
              <X className="h-4 w-4 text-gray-400" />
            </button>
          )}
        </div>
        <select
          className="border rounded-md px-3 py-2 text-sm"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setCurrentPage(1);
          }}
        >
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="sent">Sent</option>
          <option value="used">Redeemed</option>
        </select>
        <select
          className="border rounded-md px-3 py-2 text-sm"
          value={deliveryFilter}
          onChange={(e) => {
            setDeliveryFilter(e.target.value);
            setCurrentPage(1);
          }}
        >
          <option value="">All delivery types</option>
          <option value="send">Email</option>
          <option value="print">Print</option>
        </select>
      </div>

      {/* Table */}
      <div className="border rounded-lg overflow-hidden bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              {COLUMNS.map((label) => {
                const sortKey = SORT_KEYS[label];
                const active = sortKey && sortBy === sortKey;
                return (
                  <th
                    key={label}
                    className={`text-left px-4 py-3 font-medium text-gray-600 ${
                      sortKey
                        ? "cursor-pointer select-none hover:text-gray-900"
                        : ""
                    }`}
                    onClick={() => handleSort(label)}
                  >
                    {label}
                    {active ? (sortOrder === "asc" ? " ▲" : " ▼") : ""}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td
                  colSpan={COLUMNS.length}
                  className="px-4 py-8 text-center text-gray-400"
                >
                  Loading...
                </td>
              </tr>
            )}
            {!loading && gifts.length === 0 && (
              <tr>
                <td
                  colSpan={COLUMNS.length}
                  className="px-4 py-8 text-center text-gray-400"
                >
                  No gifts found
                </td>
              </tr>
            )}
            {!loading &&
              gifts.map((gift) => (
                <tr
                  key={gift.id}
                  className="border-b hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate(String(gift.id))}
                >
                  <td className="px-4 py-3 text-gray-500">{gift.id}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
                        {gift.code.substring(0, 8)}...
                      </code>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(gift.code);
                        }}
                        title="Copy full code"
                      >
                        <Copy className="h-3 w-3 text-gray-400 hover:text-gray-600" />
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3 truncate max-w-[160px]">
                    {gift.purchaserEmail}
                  </td>
                  <td className="px-4 py-3 truncate max-w-[160px]">
                    {gift.recipientEmail ?? "—"}
                  </td>
                  <td className="px-4 py-3">{gift.productDisplayName}</td>
                  <td className="px-4 py-3 capitalize">{gift.deliveryType}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[gift.status]}`}
                    >
                      {gift.status}
                    </span>
                  </td>
                  <td
                    className="px-4 py-3 text-gray-500 whitespace-nowrap"
                    title={
                      gift.legacyId
                        ? "Legacy gift (migrated) — original purchase date unavailable"
                        : undefined
                    }
                  >
                    {gift.legacyId ? "—" : formatDateTime(gift.createdAt)}
                  </td>
                  <td className="px-4 py-3 text-gray-500">
                    {formatDate(gift.scheduledAt)}
                  </td>
                  <td className="px-4 py-3">
                    <div
                      className="flex gap-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        title="Resend email"
                        className="p-1 hover:bg-gray-100 rounded"
                        onClick={() => navigate(String(gift.id))}
                      >
                        <Mail className="h-4 w-4 text-gray-500" />
                      </button>
                      <button
                        title="Card link"
                        className="p-1 hover:bg-gray-100 rounded"
                        onClick={() => navigate(String(gift.id))}
                      >
                        <ExternalLink className="h-4 w-4 text-gray-500" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500">
            Showing {(currentPage - 1) * PAGE_SIZE + 1}–
            {Math.min(currentPage * PAGE_SIZE, total)} of {total}
          </span>
          <div className="flex gap-1">
            <button
              className="px-3 py-1 border rounded text-sm disabled:opacity-50"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
            >
              Previous
            </button>
            <button
              className="px-3 py-1 border rounded text-sm disabled:opacity-50"
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
